'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTabPoll } from '../autoRefresh'
import { ChevronDown, ChevronRight, ClipboardList, Loader2, Mail, MonitorSmartphone, RefreshCw, Send, ShieldCheck, XCircle } from 'lucide-react'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses, compactDate } from '../ProcessedEntry/DomainEntry/processedTypes'
import { apiGet, apiPost } from '../Entries/entriesApi'
import { Chip } from '../Entries/autoDomainChips'
import {
  type EntryActionState, type ReportChannel, type EntryAction, type ReportResolution,
  TASK_STATUS_LABEL, TASK_STATUS_TONE, RESOLUTION_LABEL, MANUAL_CHANNELS, isManualChannel,
  canSend, canCancel, canResolve, isInFlight, describeWorker, captchaLabel, FORM_STATUS_LABEL, FORM_STATUS_TONE,
} from '../Entries/actionTypes'

// Everything the platform did about this entry, in two sections.
//
// "Takedown workers" is the planner's work: one row per worker (the
// registrar's abuse desk and its form, the hosting provider's desk and its
// form, CNCS, the fixed browser forms) with what it decided -- applies, with
// the resolved target, or not, with the reason -- and each report's life
// after the click: sent, the registrar's reply, resolved. Every worker is
// inert until launched: "Report" launches one, "Report all applicable" every
// ready one; an email is sent in the same request (Hono utils/dispatch.ts),
// a form is handed to the laptop lane, where the provider's worker module
// files it (or, with no module yet, a person does). If the entry has a
// domain and nothing has been evaluated yet, the widget asks the planner
// once, on its own, the first time it is opened.
//
// "Other actions" is the log of what an operator already did from another
// screen -- the SIBS alert, the SIBS case report, a composed email, a domain
// marked inactive. Those are never planned and never sent from here; the row
// is the record, and the only button is Resolve.
//
// Reads /actions/entry/:id; every button is forwarded to Python
// (routes/backoffice/actions.ts). Self-contained by user_entry_id, so the
// same widget sits on the entry detail, on the promoted candidate's detail
// and on the SIBS entry page.

const POLL_MS = 15000

// What the row is waiting on, and what pressing its button will set in
// motion -- one sentence per state, so the operator never has to guess what
// "Queued" means or who is going to act. Mirrors the "what happens after
// you press Report" table in the Confluence page.
export function whatNext(task: EntryAction | null, channel: ReportChannel | undefined): string {
  const email = (task?.kind ?? channel?.kind) === 'email'
  const target = task?.target ?? task?.payload?.form_url ?? null
  const impl = task?.payload?.worker_impl
  const formStatus = task?.payload?.form_status
  const via = email
    ? `send the email to ${target ?? 'the target'} through Resend, tagged [REF-…] so the answer lands on this row`
    : impl
      ? `park a browser handoff for the laptop lane, where the ${impl} worker opens ${target ?? 'the form'}, ${task?.payload?.gate_kind && task.payload.gate_kind !== 'none' ? 'clears the Cloudflare check, ' : ''}fills it from this row, solves the captcha${formStatus === 'verified' ? ', submits once' : ' and stops before Submit (dry run: a screenshot of the filled form comes back for a person to approve)'}, and the lane marks the row the moment it answers`
      : `park a browser handoff: the laptop's Chrome pane opens ${target ?? 'the form'}, fills it from this row, a person there solves the captcha, and the pane marks the row sent the moment it answers`
  if (!task) return 'Not evaluated yet — Evaluate channels asks the planner whether this channel applies.'
  switch (task.status) {
    case 'planned':
      return `Report will, right now, ${via}.`
    case 'queued':
      return `Marked queued ${fmtAgo(task.queued_at ?? task.updated_at)} but not sent -- only the manual backstop (Administration › Jobs › mitigation.dispatch › Run now) or Retry sends it now. It will then ${via}.`
    case 'sending':
      return `Being sent right now (started ${fmtAgo(task.queued_at ?? task.updated_at)}) -- it will ${via}. If this stays for more than a few minutes the request died mid-send: press Retry.`
    case 'awaiting_browser':
      return task.handoff_status === 'claimed'
        ? `The browser pane (${task.handoff_claimed_by ?? 'a session'}) has the form open and is waiting for someone at that laptop to solve the captcha; the row is marked sent the moment the pane answers.`
        : `Parked for the browser pane, not claimed yet (Administration › Jobs › handoffs). A person at that laptop solves the captcha; the form is submitted once and the pane marks the row sent the moment it answers. An unclaimed handoff expires after 4 h and fails the row. Or do it now: "Submit it yourself" opens the form in your browser.`
    case 'sent':
      return email
        ? `Sent ${fmtAgo(task.sent_at)} to ${target ?? task.email_to ?? '—'}. A reply carrying the [REF-…] tag lands here and rings the bell; ${channel?.follow_up ?? 'resolve when done'}.`
        : `Submitted ${fmtAgo(task.sent_at)}. ${channel?.follow_up ?? 'no reply comes back'} — resolve when the warning is up.`
    case 'acknowledged':
      return `A reply came back ${fmtAgo(task.acknowledged_at)} — read it in the thread and resolve.`
    case 'failed':
      return `Failed on attempt ${task.attempts} of 3${task.last_error ? `: ${task.last_error.split('\n')[0]}` : ''}. Retry queues it again; Cancel closes it.`
    case 'skipped':
      return task.payload?.form_url && !task.payload?.worker_impl
        ? `No worker files this form yet: ${task.payload?.applicability?.reason ?? 'not mapped'}. "Submit it yourself" opens it with every value ready to paste; a worker module (Administration › Actions › Providers) makes it plannable.`
        : `Nothing will be sent on this channel: ${task.payload?.applicability?.reason ?? 'not applicable'}. Re-evaluate after fixing the cause.`
    case 'resolved':
      return `Resolved ${fmtAgo(task.resolved_at)}${task.resolution ? ` as ${RESOLUTION_LABEL[task.resolution].toLowerCase()}` : ''}.`
    case 'cancelled':
      return 'Cancelled — Re-evaluate plans it again.'
    default:
      return ''
  }
}

// Where each value goes on the provider's page, for the operator doing the
// form by hand. Same map as docs/report_form_browser_runbook.md for the
// fixed forms; a provider form gets a hint from its captcha kinds.
const FORM_HINTS: Record<string, string> = {
  google_safe_browsing: 'the link opens the form with the URL already filled in · optionally paste Description into "Additional details" · tick the reCAPTCHA · Submit Report. The page answers with a "Thank you" line — paste it below.',
  cloudflare_abuse: 'Your name ← Reporter name · Your email ← Reporter email · Report title: "Phishing: <hostname> impersonating <brand>" · URLs ← URL · Description ← Description · Submit. Cloudflare shows a case number — paste it below.',
  microsoft_report: 'URL ← URL · category "Phishing" · comment ← Description · captcha · Submit. Paste what the page says below.',
  generic_form: 'Map the fields by their labels: website/URL ← URL, name/email ← Reporter, details/reason ← Description. Paste the confirmation below.',
}
const CAPTCHA_HINTS: Record<string, string> = {
  turnstile: 'a Turnstile box usually clears by itself; tick it if it asks',
  cloudflare_challenge: 'the page first shows a Cloudflare "Just a moment…" check — wait for it to clear (or tick it), then the form loads',
  recaptcha_v2: 'tick the reCAPTCHA and solve the picture grid if it asks',
  recaptcha_v2_enterprise: 'tick the reCAPTCHA and solve the picture grid if it asks',
  hcaptcha: 'solve the hCaptcha pictures',
  image_text: 'type the characters from the image',
  own: 'the provider has its own captcha step',
  login: 'the form needs an account on the provider; use the email instead',
  none: 'no captcha on this form',
}
function formHint(task: EntryAction): string {
  if (FORM_HINTS[task.channel]) return FORM_HINTS[task.channel]
  const p = task.payload ?? {}
  const steps = [
    'map the fields by their labels: website/URL ← URL, name/email/company ← Reporter, category ← Phishing, details/reason ← Description',
    p.gate_kind && p.gate_kind !== 'none' ? CAPTCHA_HINTS[p.gate_kind] : null,
    p.captcha_kind ? CAPTCHA_HINTS[p.captcha_kind] : null,
  ].filter(Boolean)
  return `${steps.join(' · ')}. Paste the confirmation below.`
}

// The link to open for a form row. Google's form pre-fills its URL field
// from ?url=; rows planned before 2026-09-16 carry the bare form URL, so
// the parameter is added here for them too.
function formLink(task: EntryAction): string | undefined {
  const base = task.payload?.form_url
  if (!base) return undefined
  const reported = task.payload?.fields?.url ?? task.payload?.url
  if (task.channel === 'google_safe_browsing' && reported && !base.includes('url=')) {
    return `${base}${base.includes('?') ? '&' : '?'}hl=en&url=${encodeURIComponent(reported)}`
  }
  return base
}

// A row the operator can take over: a web form parked for the browser pane
// that no session has finished with (or one the backstop has not parked
// yet) -- or a provider form with no worker yet, recorded as skipped WITH
// its link and fields so the operator can file it by hand.
const canSelfSubmit = (t: EntryAction) =>
  t.kind === 'browser_form' && (
    t.status === 'queued'
    || (t.status === 'awaiting_browser' && t.handoff_status !== 'fulfilled' && t.handoff_status !== 'failed')
    || (t.status === 'skipped' && !!t.payload?.form_url && !t.payload?.worker_impl))

export function fmtAgo(iso?: string | null) {
  if (!iso) return '—'
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return '—'
  const s = Math.max(0, Math.round((Date.now() - t) / 1000))
  if (s < 60) return `${s}s ago`
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}

export function ActionsWidget({
  userEntryId,
  hostname,
  hasDomain = true,
  theme,
  refreshKey,
  onChanged,
}: {
  userEntryId: string
  hostname?: string | null
  /** No domain on this entry -> no takedown channels to plan or show. */
  hasDomain?: boolean
  theme: ProcessedTheme
  refreshKey?: number
  onChanged?: () => void
}) {
  const c = watchClasses(theme)
  const isDark = theme === 'dark'
  const [state, setState] = useState<EntryActionState | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)       // action id | 'plan' | 'all'
  const [note, setNote] = useState<string | null>(null)
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const [resolving, setResolving] = useState<{ id: string; resolution: ReportResolution; note: string } | null>(null)
  // The "submit it yourself" panel: which row, what the page said, or why
  // it could not be sent.
  const [selfSubmit, setSelfSubmit] = useState<{ id: string; confirmation: string; error: string } | null>(null)
  const [copied, setCopied] = useState<string | null>(null)
  const copy = async (key: string, value: string) => {
    try { await navigator.clipboard.writeText(value); setCopied(key); setTimeout(() => setCopied(null), 1200) } catch { /* clipboard blocked: the value is on screen anyway */ }
  }
  // One automatic plan per entry, ever: set before the call, so a failure
  // shows its error and is never retried on the next poll or re-render.
  const autoPlanned = useRef<string | null>(null)

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true)
    const r = await apiGet<EntryActionState>(`/actions/entry/${encodeURIComponent(userEntryId)}`)
    if (r.success && r.data) { setState(r.data); setError(null) }
    else setError(r.error ?? 'Could not load the actions')
    setLoading(false)
  }, [userEntryId])

  useEffect(() => { load() }, [load, refreshKey])

  const tasks = useMemo(() => state?.tasks ?? [], [state])
  const catalog: ReportChannel[] = useMemo(() => state?.catalog ?? [], [state])
  const channelById = useMemo(() => new Map(catalog.map((ch) => [ch.id, ch])), [catalog])
  // A channel counts as "planned" when the catalogue does not call it manual.
  // Falling back to the hard-coded list keeps this honest if the catalogue
  // failed to load alongside the tasks.
  const isManual = useCallback((id: string) => channelById.get(id)?.manual_only ?? isManualChannel(id), [channelById])

  const inFlight = tasks.some(isInFlight)
  useTabPoll(`entry_actions_${userEntryId}`, POLL_MS, () => load(true), inFlight)

  const act = async (key: string, path: string, body: unknown, okNote: string) => {
    setBusy(key); setNote(null)
    const r = await apiPost(path, body)
    if (r.success) { setNote(okNote); onChanged?.() }
    else setNote(r.error ?? 'The action failed')
    setBusy(null)
    load(true)
  }
  const plan = () => act('plan', `/actions/entry/${encodeURIComponent(userEntryId)}/plan`, {}, 'Channels evaluated.')
  const reportAll = () => act('all', `/actions/entry/${encodeURIComponent(userEntryId)}/plan`, { send_channels: 'all' }, 'Every applicable channel sent or parked for the browser.')
  const queue = (t: EntryAction) => act(t.id, `/actions/item/${t.id}/${t.status === 'failed' ? 'retry' : 'queue'}`, {}, `${t.channel} sent.`)
  const cancel = (t: EntryAction) => act(t.id, `/actions/item/${t.id}/cancel`, {}, `${t.channel} cancelled.`)
  const resolve = async () => {
    if (!resolving) return
    await act(resolving.id, `/actions/item/${resolving.id}/resolve`, { resolution: resolving.resolution, note: resolving.note || undefined }, 'Marked resolved.')
    setResolving(null)
  }
  // The operator did the form in their own tab and says how it went. Python
  // settles the row now and closes the handoff so the laptop pane never
  // files the same report again.
  const fulfil = async (submitted: boolean) => {
    if (!selfSubmit) return
    await act(selfSubmit.id, `/actions/item/${selfSubmit.id}/fulfil`, {
      submitted,
      confirmation_text: selfSubmit.confirmation || undefined,
      error: submitted ? undefined : (selfSubmit.error || 'not submitted'),
    }, submitted ? 'Recorded as sent — thank you.' : 'Recorded as failed; Retry plans it for the browser pane again.')
    setSelfSubmit(null)
  }

  // Lazy evaluation: an entry with a domain but no planned channel gets one
  // pass of the planner the first time someone looks at it. Entries that
  // predate the planner therefore fill themselves in, instead of waiting for
  // an operator to notice the button.
  useEffect(() => {
    if (!state || !hasDomain) return
    if (autoPlanned.current === userEntryId) return
    if (tasks.some((t) => !isManual(t.channel))) return
    autoPlanned.current = userEntryId
    act('plan', `/actions/entry/${encodeURIComponent(userEntryId)}/plan`, {}, 'Channels evaluated just now.')
  }, [state, hasDomain, userEntryId, tasks, isManual]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Section A: the planner's channels, latest task per channel, in
  // catalogue order; a channel never planned shows as "not planned yet".
  const channelRows = useMemo(() => {
    const byChannel = new Map<string, EntryAction[]>()
    for (const t of tasks) byChannel.set(t.channel, [...(byChannel.get(t.channel) ?? []), t])
    return catalog.filter((ch) => !ch.manual_only).map((ch) => {
      const list = byChannel.get(ch.id) ?? []
      const live = list.filter((t) => !['failed', 'cancelled', 'skipped'].includes(t.status))
      const task = live[live.length - 1] ?? list[list.length - 1] ?? null
      return { channel: ch, task, history: list }
    })
  }, [catalog, tasks])

  // ── Section B: every manual action and the operator's own emails, newest
  // first. These are a log, not a plan, so they are listed per task and not
  // per channel — the same entry can carry three SIBS alerts.
  const manualRows = useMemo(() => {
    const manual = tasks.filter((t) => MANUAL_CHANNELS.includes(t.channel) || t.channel === 'custom_email')
    return [...manual].sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''))
  }, [tasks])

  const subject = hostname ?? tasks.find((t) => t.about)?.about ?? null
  const ready = tasks.filter((t) => canSend(t) && !isManual(t.channel))
  const planned = channelRows.some((r) => r.task)
  const evaluating = busy === 'plan'

  const btn = `inline-flex items-center gap-1 rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest transition ${c.line} ${c.muted} disabled:opacity-40`

  const thread = (task: EntryAction | null) =>
    [...(task?.events ?? []).map((e) => ({ at: e.at, who: e.actor, kind: e.kind, text: describeEvent(e.kind, e.detail) })),
     ...(task?.replies ?? []).map((r) => ({ at: r.created_at, who: r.from_address, kind: 'reply-body', text: (r.body_text ?? '').trim().slice(0, 1200) }))]
      .sort((a, b) => a.at.localeCompare(b.at))

  // The expandable body of a row: the email/form that was (or would be)
  // sent, then the events and replies in time order.
  const renderThread = (task: EntryAction | null, fallback: string, history: EntryAction[]) => {
    const t = thread(task)
    return (
      <div className={`border-t px-3 py-2 ${c.line}`}>
        {task ? (
          <>
            {task.kind === 'email' && task.payload?.subject && (
              <div className={`mb-2 font-mono text-[10px] ${c.faint}`}>
                <div><span className={c.muted}>To</span> {(task.payload.to ?? []).join(', ') || task.email_to || '—'}</div>
                <div><span className={c.muted}>Subject</span> {task.payload.subject}</div>
                {task.payload.body && <pre className={`mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded border p-2 text-[10px] ${c.line} bg-card text-muted-foreground`}>{task.payload.body}</pre>}
              </div>
            )}
            {task.kind === 'browser_form' && task.payload?.form_url && (
              <div className={`mb-2 font-mono text-[10px] ${c.faint}`}>
                <div><span className={c.muted}>Form</span> <a href={formLink(task)} target="_blank" rel="noreferrer" className="text-signal hover:underline">{task.payload.form_url}</a></div>
                {task.payload.fields?.description && <div className="mt-1 whitespace-pre-wrap">{task.payload.fields.description}</div>}
              </div>
            )}
            {t.length === 0 ? (
              <div className={`font-mono text-[10px] ${c.faint}`}>No events yet.</div>
            ) : (
              <ol className="space-y-1">
                {t.map((e, i) => (
                  <li key={i} className={`font-mono text-[10px] ${e.kind === 'reply-body' ? c.text : c.faint}`}>
                    <span className={c.muted}>{compactDate(e.at)}</span> · {e.kind === 'reply-body' ? 'reply' : e.kind}{e.who ? ` · ${e.who}` : ''}
                    {e.text ? <div className={`ml-3 whitespace-pre-wrap ${e.kind === 'reply-body' ? `mt-1 rounded border p-2 ${c.line}` : ''}`}>{e.text}</div> : null}
                  </li>
                ))}
              </ol>
            )}
            {history.length > 1 && <div className={`mt-1 font-mono text-[10px] ${c.faint}`}>{history.length - 1} earlier attempt{history.length > 2 ? 's' : ''} on this channel.</div>}
          </>
        ) : (
          <div className={`font-mono text-[10px] ${c.faint}`}>{fallback}</div>
        )}
      </div>
    )
  }

  const resolveButton = (task: EntryAction) => (
    <button type="button" data-action="actions-resolve" onClick={() => setResolving({ id: task.id, resolution: task.resolution_candidate ? 'taken_down' : 'other', note: '' })} disabled={busy != null} className={`${btn} hover:text-signal hover:border-signal`}>
      Resolve
    </button>
  )

  const resolvePanel = (task: EntryAction) => resolving?.id === task.id && (
    <div className={`flex flex-wrap items-center gap-2 border-t px-3 py-2 ${c.line}`}>
      <select value={resolving.resolution} onChange={(e) => setResolving({ ...resolving, resolution: e.target.value as ReportResolution })}
        className={`rounded border px-2 py-1 font-mono text-[11px] ${c.input}`}>
        {(Object.keys(RESOLUTION_LABEL) as ReportResolution[]).map((k) => <option key={k} value={k}>{RESOLUTION_LABEL[k]}</option>)}
      </select>
      <input value={resolving.note} onChange={(e) => setResolving({ ...resolving, note: e.target.value })} placeholder="note (optional)"
        className={`min-w-[200px] flex-1 rounded border px-2 py-1 font-mono text-[11px] ${c.input}`} />
      <button type="button" data-action="actions-resolve-confirm" onClick={resolve} className={`${btn} border-success/40 text-success`}>Confirm</button>
      <button type="button" onClick={() => setResolving(null)} className={btn}>Back</button>
    </div>
  )

  // Everything the operator needs to file the form from their own browser:
  // the link, each value with a copy button, where it goes on the page, and
  // the two ways it can end.
  const selfSubmitPanel = (task: EntryAction) => {
    if (selfSubmit?.id !== task.id) return null
    const f = task.payload?.fields ?? {}
    const values: Array<[string, string | null | undefined]> = [
      ['URL', f.url ?? task.payload?.url], ['Description', f.description],
      ['Reporter name', f.reporter_name], ['Reporter email', f.reporter_email],
      ['Company', f.company], ['Phone', f.phone], ['Country', f.country], ['Relationship', f.relationship],
      ['Category', f.abuse_category], ['Hostname', f.hostname ?? task.hostname], ['Brand', f.brand],
    ]
    const claimed = task.status === 'awaiting_browser' && task.handoff_status === 'claimed'
    return (
      <div className={`flex flex-col gap-2 border-t px-3 py-2 ${c.line}`} data-self-submit>
        {claimed && (
          <div className="font-mono text-[10px] text-warning">
            The browser pane ({task.handoff_claimed_by}) already has this form open. Filing it here as well would report the site twice — only continue if that session is stuck.
          </div>
        )}
        <div className={`text-[11px] leading-relaxed ${c.muted}`}>
          1 · <a href={formLink(task)} target="_blank" rel="noreferrer" className="text-signal hover:underline">Open the form</a> in a new tab. 2 · Copy each value into it — {formHint(task)} 3 · Submit once, and say below what the page answered.
        </div>
        <div className="grid gap-1 sm:grid-cols-2">
          {values.filter(([, v]) => v).map(([label, v]) => (
            <div key={label} className={`flex items-start gap-2 rounded border px-2 py-1 ${c.line}`}>
              <span className={`w-24 shrink-0 font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>{label}</span>
              <span className={`min-w-0 flex-1 break-words font-mono text-[10px] ${c.text}`}>{v}</span>
              <button type="button" onClick={() => copy(`${task.id}:${label}`, String(v))} className={`shrink-0 font-mono text-[9px] uppercase ${copied === `${task.id}:${label}` ? 'text-success' : `${c.faint} hover:text-signal`}`}>
                {copied === `${task.id}:${label}` ? 'copied' : 'copy'}
              </button>
            </div>
          ))}
        </div>
        <textarea
          value={selfSubmit.confirmation}
          onChange={(e) => setSelfSubmit({ ...selfSubmit, confirmation: e.target.value })}
          placeholder="What the page said after submitting (a thank-you line, a case number)…"
          rows={2}
          className={`w-full rounded border px-2 py-1 font-mono text-[11px] ${c.input}`}
          data-input="self-submit-confirmation"
        />
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" data-action="actions-self-submitted" onClick={() => fulfil(true)} disabled={busy != null}
            className={`${btn} border-success/40 text-success hover:bg-success-surface`}>
            {busy === task.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />} I submitted it
          </button>
          <input
            value={selfSubmit.error}
            onChange={(e) => setSelfSubmit({ ...selfSubmit, error: e.target.value })}
            placeholder="or: why it could not be submitted"
            className={`min-w-[200px] flex-1 rounded border px-2 py-1 font-mono text-[11px] ${c.input}`}
          />
          <button type="button" data-action="actions-self-failed" onClick={() => fulfil(false)} disabled={busy != null || !selfSubmit.error.trim()}
            className={`${btn} hover:text-destructive hover:border-destructive`}>
            Could not submit
          </button>
          <button type="button" onClick={() => setSelfSubmit(null)} className={btn}>Back</button>
        </div>
      </div>
    )
  }

  const sectionLabel = `flex items-center gap-2 font-mono text-[9px] uppercase tracking-widest ${c.faint}`

  return (
    <div className="flex flex-col gap-2" data-actions-widget>
      <div className={`flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>
        <ShieldCheck className="h-3 w-3 shrink-0" />
        Actions taken{subject ? <span className={c.text}> · {subject}</span> : null}
        <span className="flex-1" />
        {loading && <Loader2 className="h-3 w-3 animate-spin" />}
        <button type="button" onClick={() => load(true)} className={btn} title="Refresh"><RefreshCw className="h-3 w-3" /></button>
        {hasDomain && (
          <>
            <button type="button" data-action="actions-plan" onClick={plan} disabled={busy != null} className={`${btn} hover:text-signal hover:border-signal`}>
              {evaluating ? <Loader2 className="h-3 w-3 animate-spin" /> : null} {planned ? 'Re-evaluate' : 'Evaluate channels'}
            </button>
            <button type="button" data-action="actions-report-all" onClick={reportAll} disabled={busy != null || ready.length === 0}
              className={`${btn} border-success/40 text-success hover:bg-success-surface`}>
              {busy === 'all' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Report all applicable{ready.length ? ` (${ready.length})` : ''}
            </button>
          </>
        )}
      </div>

      {hasDomain && (
        <p className={`text-[11px] leading-relaxed ${c.muted}`}>
          Nothing is sent by itself. Each worker below says whether it applies, to whom, and what <span className={c.text}>Report</span> does: an email is sent through Resend before the page answers; a web form is handed to the laptop lane, where the provider's worker module files it (a verified one submits, a new one dry-runs and sends back a screenshot) or, with no module yet, a person does. The registrar and the hosting provider each get both routes. Replies come back to the row.</p>
      )}

      {error && <div className="font-mono text-[11px] text-destructive">{error}</div>}
      {note && <div className={`font-mono text-[11px] ${note.includes('fail') || note.includes('Could not') ? 'text-destructive' : 'text-success'}`} data-actions-note>{note}</div>}

      {/* ── A · the planner's channels ─────────────────────────────── */}
      {hasDomain && (
        <div className="flex flex-col gap-1.5">
          <div className={sectionLabel}><ShieldCheck className="h-3 w-3" /> Takedown workers</div>

          {evaluating && (
            <div className={`rounded border px-3 py-2 font-mono text-[11px] ${c.line} ${c.faint}`}>
              <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Evaluating channels…
            </div>
          )}

          {!loading && !evaluating && !planned && channelRows.length === 0 && (
            <div className={`rounded border px-3 py-2 font-mono text-[11px] ${c.line} ${c.faint}`}>No channel evaluated.</div>
          )}

          {channelRows.map(({ channel, task, history }) => {
            const app = task?.payload?.applicability
            const isOpen = !!open[channel.id]
            return (
              <div key={channel.id} data-channel={channel.id} className={`rounded border ${c.line} ${c.soft}`}>
                <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                  <button type="button" onClick={() => setOpen((o) => ({ ...o, [channel.id]: !isOpen }))} className={`${c.faint} hover:${c.text}`} title="Thread">
                    {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  </button>
                  {channel.kind === 'email' ? <Mail className={`h-3 w-3 ${c.faint}`} /> : <MonitorSmartphone className={`h-3 w-3 ${c.faint}`} />}
                  <span className={`font-mono text-[12px] ${c.text}`}>{channel.label}</span>
                  {task ? <Chip label={TASK_STATUS_LABEL[task.status]} tone={TASK_STATUS_TONE[task.status]} /> : <Chip label="not planned" tone="neutral" />}
                  {task?.payload?.provider && <Chip label={task.payload.provider} tone="neutral" title={`${task.payload.provider_role ?? 'provider'} behind this worker`} />}
                  {task?.kind === 'browser_form' && task.payload?.gate_kind && task.payload.gate_kind !== 'none' && <Chip label={`gate: ${captchaLabel(task.payload.gate_kind)}`} tone="amber" title="A check in front of the page (indicative)" />}
                  {task?.kind === 'browser_form' && task.payload?.captcha_kind && task.payload.captcha_kind !== 'none' && <Chip label={captchaLabel(task.payload.captcha_kind)} tone="neutral" title="The captcha inside the form (indicative)" />}
                  {task?.kind === 'browser_form' && task.payload?.form_status && <Chip label={FORM_STATUS_LABEL[task.payload.form_status]} tone={FORM_STATUS_TONE[task.payload.form_status]} title={task.payload.worker_impl ? `worker module ${task.payload.worker_impl}` : 'no worker module for this form yet'} />}
                  {task?.resolution && <Chip label={RESOLUTION_LABEL[task.resolution]} tone="emerald" />}
                  {task?.resolution_candidate && task.status !== 'resolved' && <Chip label="reply says taken down?" tone="amber" title="The reply reads like a takedown — confirm and mark resolved" />}
                  <span className={`min-w-0 flex-1 truncate font-mono text-[10px] ${c.faint}`} title={app?.reason ?? ''}>
                    {task?.target ? <span className={c.muted}>{task.target}</span> : null}
                    {task?.target && app?.reason ? ' · ' : ''}
                    {app?.reason ?? channel.follow_up}
                  </span>
                  {task?.sent_at && <span className={`font-mono text-[10px] ${c.faint}`}>sent {fmtAgo(task.sent_at)}</span>}
                  {task?.last_error && task.status === 'failed' && <span className="font-mono text-[10px] text-destructive" title={task.last_error}>{task.last_error.slice(0, 60)}</span>}
                  {task && canSend(task) && (
                    <button type="button" data-action="actions-send" onClick={() => queue(task)} disabled={busy != null} className={`${btn} border-success/40 text-success hover:bg-success-surface`}>
                      {busy === task.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} {task.status === 'failed' ? 'Retry' : 'Report'}
                    </button>
                  )}
                  {task && canSelfSubmit(task) && (
                    <button type="button" data-action="actions-self-submit" onClick={() => setSelfSubmit(selfSubmit?.id === task.id ? null : { id: task.id, confirmation: '', error: '' })} disabled={busy != null}
                      className={`${btn} hover:text-signal hover:border-signal`} title="Open the form in your own browser and file it yourself">
                      <MonitorSmartphone className="h-3 w-3" /> Submit it yourself
                    </button>
                  )}
                  {task && canResolve(task) && resolveButton(task)}
                  {task && canCancel(task) && (
                    <button type="button" data-action="actions-cancel" onClick={() => cancel(task)} disabled={busy != null} className={`${btn} hover:text-destructive hover:border-destructive`} title="Cancel">
                      <XCircle className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <div className={`-mt-1 px-3 pb-2 pl-9 text-[10px] leading-relaxed ${c.faint}`} data-next>
                  {describeWorker(task) ? <><span className={c.muted}>Worker</span> · {describeWorker(task)}<br /></> : null}
                  <span className={c.muted}>Next</span> · {whatNext(task, channel)}
                </div>
                {task && selfSubmitPanel(task)}
                {task && resolvePanel(task)}
                {isOpen && renderThread(task, channel.follow_up, history)}
              </div>
            )
          })}
        </div>
      )}

      {/* ── B · what an operator already did elsewhere ─────────────── */}
      <div className="flex flex-col gap-1.5">
        <div className={sectionLabel}><ClipboardList className="h-3 w-3" /> Other actions</div>

        {manualRows.length === 0 ? (
          <div className={`rounded border px-3 py-2 font-mono text-[11px] ${c.line} ${c.faint}`}>No other actions yet.</div>
        ) : manualRows.map((task) => {
          const channel = channelById.get(task.channel)
          const isOpen = !!open[task.id]
          const isCustomEmail = task.channel === 'custom_email'
          return (
            <div key={task.id} data-channel={task.channel} className={`rounded border ${c.line} ${c.soft}`}>
              <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                <button type="button" onClick={() => setOpen((o) => ({ ...o, [task.id]: !isOpen }))} className={`${c.faint} hover:${c.text}`} title="Thread">
                  {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                </button>
                {task.kind === 'email' ? <Mail className={`h-3 w-3 ${c.faint}`} /> : task.kind === 'browser_form' ? <MonitorSmartphone className={`h-3 w-3 ${c.faint}`} /> : <ClipboardList className={`h-3 w-3 ${c.faint}`} />}
                <span className={`font-mono text-[12px] ${c.text}`}>{channel?.label ?? task.channel}</span>
                <Chip label={TASK_STATUS_LABEL[task.status]} tone={TASK_STATUS_TONE[task.status]} />
                {task.resolution && <Chip label={RESOLUTION_LABEL[task.resolution]} tone="emerald" />}
                {task.email_status && <Chip label={task.email_status} tone={task.email_status === 'failed' ? 'red' : 'neutral'} title="Delivery status of the email that was sent" />}
                <span className={`min-w-0 flex-1 truncate font-mono text-[10px] ${c.faint}`} title={task.target ?? task.about ?? ''}>
                  {task.target ?? task.email_to ?? task.about ?? '—'}
                </span>
                {task.sent_at && <span className={`font-mono text-[10px] ${c.faint}`}>sent {fmtAgo(task.sent_at)}</span>}
                {task.last_error && task.status === 'failed' && <span className="font-mono text-[10px] text-destructive" title={task.last_error}>{task.last_error.slice(0, 60)}</span>}
                {/* A manual action is a record of something already done, so
                    there is nothing to send or cancel — except the operator's
                    own email, which is queued here like any other report. */}
                {isCustomEmail && canSend(task) && (
                  <button type="button" data-action="actions-send" onClick={() => queue(task)} disabled={busy != null} className={`${btn} border-success/40 text-success hover:bg-success-surface`}>
                    {busy === task.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} {task.status === 'failed' ? 'Retry' : 'Report'}
                  </button>
                )}
                {canResolve(task) && resolveButton(task)}
                {isCustomEmail && canCancel(task) && (
                  <button type="button" data-action="actions-cancel" onClick={() => cancel(task)} disabled={busy != null} className={`${btn} hover:text-destructive hover:border-destructive`} title="Cancel">
                    <XCircle className="h-3 w-3" />
                  </button>
                )}
              </div>
              {isCustomEmail && (
                <div className={`-mt-1 px-3 pb-2 pl-9 text-[10px] leading-relaxed ${c.faint}`} data-next>
                  <span className={c.muted}>Next</span> · {whatNext(task, channel)}
                </div>
              )}
              {resolvePanel(task)}
              {isOpen && renderThread(task, channel?.follow_up ?? 'Logged action.', [task])}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function describeEvent(kind: string, d: Record<string, any> | null | undefined): string {
  const x = d ?? {}
  switch (kind) {
    case 'planned': return x.target ? `target ${x.target}${x.source ? ` (${x.source})` : ''}` : (x.reason ?? '')
    case 'queued': return ''
    case 'sent': return `to ${(x.to ?? []).join(', ')}${x.subject ? ` — ${x.subject}` : ''}${x.images ? ` · ${x.images} screenshot(s)` : ''}`
    case 'handoff_parked': return `browser form ${x.form_url ?? ''}`
    case 'handoff_fulfilled': return x.confirmation_text ?? 'submitted'
    case 'captcha': return `${captchaLabel(x.captcha_kind)} · ${x.outcome === 'solved' ? `solved automatically${x.elapsed_s ? ` in ${x.elapsed_s}s` : ''}` : x.outcome === 'unsupported' ? 'needs a person' : `failed${x.detail ? ` — ${x.detail}` : ''}`}`
    case 'worker': return `${x.impl ?? 'worker'}${x.dry_run ? ' · dry run (filled, not submitted)' : ''}${x.error_kind === 'selector' ? ' · form changed: a field was not found (provider row demoted to dry run)' : x.error_kind ? ` · ${x.error_kind}` : ''}${x.gate?.kind ? ` · cleared ${captchaLabel(x.gate.kind)}` : ''}`
    case 'reply': return `${x.from ?? ''}${x.takedown_hint ? ' · reads like a takedown' : ''}${x.excerpt ? ` — ${x.excerpt}` : ''}`
    case 'status_change': return `${x.to ?? ''}${x.resolution ? ` · ${x.resolution}` : ''}${x.note ? ` — ${x.note}` : ''}`
    case 'error': return x.error ?? ''
    case 'note': return x.note ?? ''
    default: return JSON.stringify(x)
  }
}
